/* Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved. */

import { Bath, BedDouble, Car } from 'lucide-react';
import type { PropertyResponseType } from '@services/housien/types';
import { Text } from '@/shared/ui';

type Spec = { key: string; icon: typeof Bath; value: number };

/**
 * Reads the three counts a buyer scans for, dropping the ones the listing
 * never filled in — a missing parking count should read as absent, not as
 * zero spaces.
 *
 * @param property - The listing row.
 * @returns The specs that have a value, in quartos / banheiros / vagas order.
 */
function readSpecs(property: PropertyResponseType): Spec[] {
  const c = property.characteristics as
    | { bedrooms?: number; bathrooms?: number; parking_spots?: number }
    | undefined;
  return [
    { key: 'bedrooms', icon: BedDouble, value: c?.bedrooms ?? 0 },
    { key: 'bathrooms', icon: Bath, value: c?.bathrooms ?? 0 },
    { key: 'parking', icon: Car, value: c?.parking_spots ?? 0 },
  ].filter((spec) => spec.value > 0);
}

/**
 * The quartos / banheiros / vagas run that shares the row's second line with
 * the city. Icons rather than words because the line has to survive a narrow
 * viewport without pushing the city out of the row.
 *
 * @param props.property - The listing row.
 * @param props.t - Translator for the icon tooltips.
 * @returns The spec run, or null when the listing declares none of the three.
 */
export function PropertySpecs(props: {
  property: PropertyResponseType;
  t: (key: string) => string;
}) {
  const specs = readSpecs(props.property);
  if (specs.length === 0) return null;

  return (
    <Text
      component="span"
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 'var(--space-2)',
        flexShrink: 0,
        fontVariantNumeric: 'tabular-nums',
        color: 'var(--text-secondary)',
      }}
    >
      {specs.map(({ key, icon: Icon, value }) => (
        <Text
          component="span"
          key={key}
          title={props.t(`list.specs.${key}`)}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}
        >
          <Icon width={13} height={13} strokeWidth={2} aria-hidden="true" />
          {value}
        </Text>
      ))}
    </Text>
  );
}
